import {
    Column,
    DateCol,
    GenericStatusCol,
    Option,
    RenderStringCol,
    RenderStringCol2,
    StringCol,
    Table,
    ViewOnly,
} from "../../../misc/scroller/Table";
import {api} from "../../../api/API";
import {useTabQueryParam} from "../../../misc/userpref/useTabQueryParam";
import {PurchaseOrder} from "../../../api/Logistics";
import {projectLabel} from "../../../api/Projects";
import {formatCents} from "nate-react-api-helpers";
import {SectionHeader} from "../../../misc/SectionHeader";
import {brown, deepOrange, green, grey, orange, pink, purple, yellow} from "@mui/material/colors";
import React, {useEffect, useState} from "react";
import moment from "moment";
import { SmallSearchBox } from "./SmallSearchBox";
import {ProjectFilter} from "./ProjectFilter";
import {Grid, Tab, Tabs} from "@mui/material";
import {removeRowOption, RowOptionCol} from "./RowOptions";
import { CreatePurchaseOrder } from "./CreatePurchaseOrder";
import {Link, useNavigate} from "react-router-dom";
import {Container} from "../../../misc/Container";
import {WhiteColumn} from "./WhiteColumn";
import {SmallGreyButton} from "../../../misc/SmallGreyButton";
import {OpenInNew} from "@mui/icons-material";
import {ToSourceProps} from "./PartsToSource";
import { PopoutInteractor } from "../../../misc/PopoutInteractor";
import {TabWrapper} from "../../project/shopdrawing/TabWrapper";
import {TaskBoard} from "../../tasks/TaskBoard";
import {getAll} from "../routes/useGetAll";
import {PageTitle} from "../../../misc/PageTitle";
import {Config} from "../../../api/ConfigureSourcing";
import {DateFilterCol, WithDateFilter} from "../../../misc/DateFilterForCol";

export const purchaseOrderStatuses = [
    {
        display: "Draft",
        value: "draft",
    },
    {
        display: "Price Request",
        value: "price-request",
    },
    {
        display: "Submitted",
        value: "submitted",
    },
    {
        display: "Ready for pickup",
        value: "ready-for-pickup",
    },
    {
        display: "Assigned Route",
        value: "assigned-route",
        disabled: true,
    },
    {
        display: "In Truck",
        value: "in-truck",
        disabled: true,
    },
    {
        display: "Pending Receiving",
        value: "pending-receiving",
        disabled: true,
    },
    {
        display: "Received",
        value: "complete",
    },
    {
        display: "Back Ordered",
        value: "backorder",
    },
];

export const pickup = "pickup"
export const deliverToSite = "delivered-to-site"

export const deliveryMethods = [
    {
        display: "Pickup",
        value: pickup,
    },
    {
        display: "Delivery",
        value: "delivery",
    },
    {
        display: "Delivered to Site",
        value: deliverToSite,
    }
];

export const purchaseOrderTableName = "purchasing.purchase-orders";
export const purchasingPopoutPanel = new PopoutInteractor<ToSourceProps | "refresh-to-source", "refresh-po">("purchasing.popout", "/logistics/purchasing/to-source-popout", {maxWidth: 1080});

export const statusMap = {
    "draft": {
        display: "Draft",
        color: orange["400"],
    },
    "price-request": {
        display: "Price Request",
        color: pink["700"],
        textColor: "white",
    },
    "backorder": {
        display: "Back Order",
        color: purple["400"],
        textColor: "white",
    },
    "submitted": {
        display: "Submitted",
        textColor: green["800"],
    },
    "ready-for-pickup": {
        display: "Ready for Pickup",
        textColor: "white",
        color: green["400"],
    },
    "assigned-route": {
        display: "Assigned Route",
        textColor: "white",
        color: brown["400"],
    },
    "in-truck": {
        display: "In Truck",
        color: yellow["400"],
        textColor: grey["900"],
    },
    "complete": {
        display: "Complete",
        textColor: grey["400"],
    }
};

const payableStatusMap = {
    "unpaid": {
        display: "Unpaid",
        textColor: grey["400"],
    },
    "partial": {
        display: "Partial",
        textColor: yellow["800"],
    },
    "paid": {
        display: "Paid",
        textColor: green["800"],
    }
}

export function PurchaseOrderList(props: {}) {
    const [search, setSearch] = useState("")
    const [project, setProject] = useState<Option|null>(null)
    const navigate = useNavigate();

    useEffect(() => {
        purchasingPopoutPanel.sendToClient({
            isDraft: false,
        });

        return () => {
            purchasingPopoutPanel.close();
        }
    }, []);

    const [tab, setTab, dt] = useTabQueryParam({page: "logistics.purchasing", urlKey: "t", fallback: "active", allowed: ["active", "all", "configs", "tasks"]});

    return <Container>
        <WhiteColumn>
            <PageTitle title={"Purchasing"} />
            <SectionHeader name="Purchasing" right={
                <Grid container spacing={1} flexWrap="nowrap">
                    <Grid item>
                        <SmallGreyButton onClick={() => purchasingPopoutPanel.open()} startIcon={<OpenInNew />}>
                            To Source
                        </SmallGreyButton>
                    </Grid>
                    <Grid item>
                        <ProjectFilter value={project} onChange={setProject} />
                    </Grid>
                    <Grid item>
                        <SmallSearchBox value={search} onChange={setSearch} fullWidth />
                    </Grid>
                </Grid>
            } />

            <TabWrapper>
                <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                    <Tab label="Active" value="active" {...dt.tabProps("active")} />
                    <Tab label="All POs" value="all" {...dt.tabProps("all")} />
                    <Tab label="Configs" value="configs" {...dt.tabProps("configs")} />
                    <Tab label="Tasks" value="tasks" {...dt.tabProps("tasks")} />
                </Tabs>
                {dt.controls}
            </TabWrapper>

            {tab === "tasks" && <TaskBoard boardKey="purchasing" />}

            {(tab === "all" || tab === "active") && <Table<PurchaseOrder>
                name={purchaseOrderTableName}
                cellCustomize={{
                    backgroundColor: (input: PurchaseOrder, col: Column<any>) => {
                        if(input.linkedProjects?.some(s => s.needsRecalculateProducts)) {
                            if(["draft", "price-request", "backorder", "submitted"].indexOf(input.status) !== -1) {
                                return deepOrange["100"];
                            }
                        }
                    }
                }}
                columns={[
                    RowOptionCol({
                        options: (dt) => [
                            removeRowOption(dt, {
                                refreshTables: [purchaseOrderTableName],
                                confirm: true,
                            })
                        ],
                    }),
                    ViewOnly(StringCol("PO #", "poNumber", 100)),
                    ViewOnly(StringCol("Supplier", "supplierName", 100)),
                    RenderStringCol<PurchaseOrder>("Project", (dt) => dt.linkedProjects?.map(v => {
                        if(v.needsRecalculateProducts) {
                            return v.name + " *Warning*"
                        }

                        return v.name;
                    }).join(", ") || "", 200),
                    DateFilterCol<PurchaseOrder>("Submitted", "submittedAt", "MMM D, YYYY", 100),
                    WithDateFilter<PurchaseOrder>(DateCol("Target", "targetPickupDate", "MMM D, YYYY", 100), "targetPickupDate", "future"),
                    WithDateFilter<PurchaseOrder>(RenderStringCol<PurchaseOrder>("Needed By", dt =>
                        dt.neededBy ? moment.utc(dt.neededBy).format("MMM D, YYYY") + (dt.neededByHasMultiple ? " *" : "") : "", 120), "neededBy", "future"),
                    GenericStatusCol("Status", "status", statusMap),
                    GenericStatusCol("Bill Status", "payableStatus", payableStatusMap),
                    RenderStringCol<PurchaseOrder>("Bills", p => p.linkedPayables?.map(v => "#" + v.vendorRef)?.join(", ") || ""),
                    {
                        name: "Amount",
                        render: dt => formatCents(dt.totalCents),
                        alignRight: true,
                        width: 70,
                        filter: (row, value) => {
                            return formatCents(row.totalCents).indexOf(value) === 0;
                        },
                    },
                    StringCol("Notes", "notes", 100),
                ]}
                fetch={ctx => getAll(ctx, offset => api.logistics.listPurchaseOrders({
                    offset,
                    search,
                    project: project?.value || undefined,
                    includeInActive: tab === "all",
                }))}
                onChange={input => api.logistics.updatePurchaseOrder(input)}
                fetchDeps={[search, project, tab]}
                onRowClick={(item) => {
                    navigate("/logistics/purchasing/" + item.id);
                }}
                insert={{
                    modal: (onDone) => <CreatePurchaseOrder onDone={onDone} />,
                }}
            />}

            {tab === "configs" && <Table<Config>
                name={sourceConfigTableName}
                columns={[
                    RowOptionCol({
                        options: (dt) => [
                            removeRowOption(dt, {
                                refreshTables: [sourceConfigTableName],
                                confirm: true,
                            })
                        ],
                    }),
                    ViewOnly(StringCol("Config #", "id", 100)),
                    ViewOnly(StringCol("Supplier", "supplierName", 100)),
                    ViewOnly(RenderStringCol<Config>("Project", d => projectLabel({projectNumber: d.projectNumber, name: d.projectName}), 200)),
                    ViewOnly(GenericStatusCol("Completed", "complete", {
                        "true": {
                            display: "Complete",
                            textColor: green["800"],
                        },
                        "false": {
                            display: "Draft",
                            color: orange["300"],
                        }
                    })),
                    RenderStringCol2<Config>("Tykel PO",
                        r => r.tykelPoNumber?.toString() || "",
                        r => r.tykelPoNumber ? <Link onClick={e => {
                            e.stopPropagation()
                        }} to={`/logistics/purchasing/${r.poTykel}`}>#{r.tykelPoNumber}</Link> : null
                    ),
                    RenderStringCol2<Config>("Supplier PO",
                        r => r.supplierPoNumber?.toString() || "",
                        r => r.supplierPoNumber ? <Link onClick={e => {
                            e.stopPropagation()
                        }} to={`/logistics/purchasing/${r.poSupplier}`}>#{r.supplierPoNumber}</Link> : null
                    ),
                ]}
                fetch={ctx => getAll(ctx, offset => api.configureSourcing.list({
                    offset,
                    search,
                    project: project?.value || undefined,
                }))}
                onChange={input => api.configureSourcing.upsert(input)}
                fetchDeps={[search, project, tab]}
                onRowClick={(item) => {
                    navigate("/logistics/purchasing/configure-sourcing/" + item.id);
                }}
                insert={{
                    modal: (onDone) => <CreatePurchaseOrder onDone={onDone} />,
                }}
            />}
        </WhiteColumn>
    </Container>
}


const sourceConfigTableName = "source-configs";
